import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqData } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9osluaqs38sj6jc7eqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '57漫画网',
    url: 'https://57manhua.com/',
    description: '中国领先的免费在线漫画阅读平台，提供蜜桃少年漫画等海量优质漫画。',
    email: 'contact@57manhua.com',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      telephone: '+86-10-88888888',
      contactOption: 'TollFree',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">关于我们</h1>
            <p className="text-sm text-foreground-500 mt-2">了解57漫画网和蜜桃少年漫画</p>
          </div>

          <section className="mt-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">品牌介绍</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    57漫画网成立于2020年，是国内领先的免费在线漫画阅读平台。我们致力于为广大漫画爱好者提供最优质的阅读体验，汇集了包括<strong>蜜桃少年漫画</strong>在内的数千部热门漫画作品。
                  </p>
                  <p>
                    作为一家专注于漫画内容分发的互联网平台，57漫画网始终坚持以用户为中心，不断优化阅读体验。我们的核心理念是让每一位漫画爱好者都能便捷地享受到高质量的漫画内容。
                  </p>
                  <p>
                    <strong className="text-foreground-950">蜜桃少年漫画</strong>是57漫画网的主打作品之一，由知名漫画家桃花仙人创作。这部作品以其精美的画风、扣人心弦的剧情和深刻的人物塑造，赢得了广大读者的喜爱和认可。
                  </p>
                  <p>
                    目前，57漫画网已累计收录超过5000部漫画作品，月活跃用户突破1200万。我们与国内多家漫画工作室和独立漫画家建立了深度合作关系，持续为用户带来最新、最热的漫画内容。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20reading%20nook%20with%20a%20tablet%20displaying%20colorful%20anime%20manga%20art%2C%20surrounded%20by%20manga%20books%20and%20a%20cup%20of%20tea%2C%20soft%20natural%20lighting%20from%20a%20nearby%20window%2C%20warm%20wooden%20furniture%2C%20comfortable%20atmosphere%2C%20clean%20aesthetic%20composition&width=1200&height=900&seq=about-brand-image&orientation=landscape"
                  alt="57漫画网品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来自真实用户的评价</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <div key={j} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">contact@57manhua.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-customer-service-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">在线客服</p>
                      <p className="text-sm font-medium text-foreground-950">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-950">中国·北京市朝阳区</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">在线留言</h2>
                {formStatus === 'success' ? (
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-8 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950 mb-2">留言已提交</h3>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您！</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 space-y-4" data-readdy-form="">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="spam-guard">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    {formStatus === 'error' && (
                      <p className="text-sm text-accent-600">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>

          <section className="mt-14 mb-4">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">常见问题</h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqData.map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                    <span className="text-sm md:text-base font-medium text-foreground-950">{faq.question}</span>
                    <div className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}